import { useContextSelector } from 'use-context-selector';
import { AppContext } from './index';
import { AppContextSelectors } from './selectors';
import { Business } from '../types/business';
import { ServiceType } from '../types/user';

export const useBusiness = (businessId: string): Business | undefined => {
  return useContextSelector(AppContext, AppContextSelectors.makeGetBusiness(businessId));
};

export const useSubscription = (groupId: string) => {
  return useContextSelector(AppContext, AppContextSelectors.makeGetSubscription(groupId));
};

export const useLoading = (): [loading: boolean, setLoading: (show: boolean) => void] => {
  // @ts-ignore
  return useContextSelector(AppContext, AppContextSelectors.getLoading);
};

export const useUpdateSubscription = () => {
  return useContextSelector(AppContext, (context) => context.updateSubscription);
};

export const useReloadCurrentUser = () => {
  return useContextSelector(AppContext, (context) => context.reloadCurrentUser);
};

export const useIsLoggedIn = () => {
  return useContextSelector(AppContext, (context) => !!context.currentUser);
};

export const useClearSessionAndRedirectToLogin = () => {
  return useContextSelector(AppContext, (context) => context.clearSessionAndRedirectToLogin);
};

export const useBroadcastLogout = () => {
  return useContextSelector(AppContext, (context) => context.broadcastLogout);
};

export const useUpdateGroup = () => {
  return useContextSelector(AppContext, (context) => context.updateGroup);
};

export const useFamilies = () => {
  return useContextSelector(AppContext, (context) => context.currentUser?.families || []);
};

export const useBusinesses = () => {
  return useContextSelector(AppContext, (context) => context.currentUser?.businesses || []);
};

export const useCurrentUser = () => {
  return useContextSelector(AppContext, (context) => context.currentUser);
};

export const useLanguage = () => {
  return useContextSelector(AppContext, (context) => context.language);
};

export const useUpdateLanguage = () => {
  return useContextSelector(AppContext, (context) => context.updateLanguage);
};

export const useSetLoading = () => {
  return useContextSelector(AppContext, (context) => context.setIsLoading);
};

export const useReloadTree = () => {
  return useContextSelector(AppContext, (context) => context.reloadTree);
};

export const useIsManagementUser = () => {
  return useContextSelector(AppContext, (context) => context.currentUser?.serviceType === ServiceType.PINGPAD_MANAGEMENT);
};

/** Management user with SUBSCRIPTION_ADMIN content role for at least one group (JWT permissions). */
export const useIsSubscriptionAdministrator = () => {
  return useContextSelector(
    AppContext,
    (context) =>
      context.currentUser?.serviceType === ServiceType.PINGPAD_MANAGEMENT &&
      Boolean(context.currentUser?.permissions?.some((p) => p.contentPermissions?.includes('SUBSCRIPTION_ADMIN'))),
  );
};

export const useGroupPermissions = (groupId: string | undefined): string[] | null => {
  return useContextSelector(AppContext, (context) => {
    if (!groupId) {
      return null;
    }
    // If permissions array doesn't exist, return null (backward compatibility - show everything)
    if (!context.currentUser?.permissions) {
      return null;
    }
    const permission = context.currentUser.permissions.find((p) => p.groupId === groupId);
    // If no permission entry for this group, return null (backward compatibility - show everything)
    if (!permission) {
      return null;
    }
    // Subscription Administrator content role: full content access (same semantics as null below)
    if (permission.contentPermissions?.includes('SUBSCRIPTION_ADMIN')) {
      return null;
    }
    // Return the contentPermissions array (can be empty)
    return permission.contentPermissions || [];
  });
};
